import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import DailyReportItem from '../../components/common/DailyReport/DairyReportItem';
import {
  Box,
  Fab,
  Paper,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
} from '@material-ui/core';
import { SCREENS } from '../../constants/Screens';
import { DailyReport, DailyReportModel } from '../../models/DailyReport/DailyReportModel';
import { DailyReportHeaderContext } from '../../navigation/tabStack/DailyReportTabStack';
import { useFocusEffect } from '@react-navigation/native';
import { User, UserModel } from '../../models/UserModel';
import { LocalStorageKeys, LocalStorageService } from '../../services/LocalStorageService.';

import { DailyReportReadModel } from '../../models/DailyReport/DailyReportReadModel';
import { toast } from 'react-toastify';

/**********************************
 * context
 */
//日報コメント展開
interface DailyReportCommentOpenContextProps {
  showComments: boolean;
  setShowComments: (e: boolean) => void;
}

export const DailyReportCommentOpenContext =
  React.createContext<DailyReportCommentOpenContextProps>({} as DailyReportCommentOpenContextProps);

//日報請求書展開
interface DailyReportInvoiceOpenContextProps {
  isExpandedInvoice: boolean;
  setExpandedInvoice: (e: boolean) => void;
}

export const DailyReportInvoiceOpenContext =
  React.createContext<DailyReportInvoiceOpenContextProps>({} as DailyReportInvoiceOpenContextProps);

//日報一覧
interface DailyReportsContextProps {
  dailyReports: DailyReport[];
  setDailyReports: (e: DailyReport[]) => void;
}

export const DailyReportsContext = React.createContext<DailyReportsContextProps>(
  {} as DailyReportsContextProps
);

/**********************************
 *   日報一覧画面
 * ********************************
 */
const DailyReportScreen = () => {
  //ローカルストレージ呼び出し
  const loadSelectedUsers = LocalStorageService.get<string[]>(
    LocalStorageKeys.Daily_Report.Selected_Users
  );

  const loadSelectedMemberType = LocalStorageService.get<string>(
    LocalStorageKeys.Daily_Report.Selected_Member_Type
  );

  const loadSelectedOpenComment = LocalStorageService.get<boolean>(
    LocalStorageKeys.Daily_Report.Open_Comment
  );

  const loadSelectedExpandInvoice = LocalStorageService.get<boolean>(
    LocalStorageKeys.Daily_Report.Expand_Invoice
  );

  const loadSelectedUnread = LocalStorageService.get<boolean>(
    LocalStorageKeys.Daily_Report.Only_Unread
  );

  // ヘッダーイベント検知
  const { addListener, removeListener } = React.useContext(DailyReportHeaderContext);

  // ページング
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  //日報
  const [dailyReports, setDailyReports] = useState<DailyReport[]>([]);

  // スクロール検知
  const observerRef = useRef<HTMLDivElement | null>(null);

  // フィルタリング選択肢
  const [users, setUsers] = useState<User[]>([]);
  // 削除モーダル state
  const [openAllReadModal, setAllReadModal] = useState(false);

  // フィルタリング条件
  const [filterMemberType, setFilterMemberType] = useState<string>(
    loadSelectedMemberType ?? 'default'
  );
  const [selectedUsers, setSelectedUsers] = useState<string[]>(loadSelectedUsers ?? []);

  const [showComments, setShowComments] = useState<boolean>(loadSelectedOpenComment ?? true);

  const [isExpandedInvoice, setExpandedInvoice] = useState<boolean>(
    loadSelectedExpandInvoice ?? false
  );
  const [isUnreadOnly, setIsUnreadOnly] = useState<boolean>(loadSelectedUnread ?? false);

  const [selectedDate, setSelectedDate] = useState<string>('');

  //メンバータイプ選択
  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const memberType = event.target.value as string;
    LocalStorageService.set(LocalStorageKeys.Daily_Report.Selected_Member_Type, memberType);

    setFilterMemberType(memberType);
  };

  //コメント開閉
  const handleCommentToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const e = event.target.checked as boolean;
    LocalStorageService.set(LocalStorageKeys.Daily_Report.Open_Comment, e);
    setShowComments(e);
  };

  const handleInvoiceToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const e = event.target.checked as boolean;
    LocalStorageService.set(LocalStorageKeys.Daily_Report.Expand_Invoice, e);
    setExpandedInvoice(e);
  };

  const handleUnreadToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const e = event.target.checked as boolean;
    LocalStorageService.set(LocalStorageKeys.Daily_Report.Only_Unread, e);
    setIsUnreadOnly(e);
  };

  //ユーザー選択
  const handleUserChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const userIds = event.target.value as string[];
    LocalStorageService.set(LocalStorageKeys.Daily_Report.Selected_Users, userIds);
    setSelectedUsers(event.target.value as string[]);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value);
  };

  /**
   *   レポートボタン
   */
  const onhandleReportButton = () => {
    window.navi.navigate(SCREENS.DailyReport.Detail);
  };

  //   日報取得
  const fetchDailyReports = async () => {
    if (isLoading || (lastPage !== null && currentPage > lastPage)) return;

    setIsLoading(true);
    try {
      const paginate = await DailyReportModel.fetchList({
        page: currentPage,
        user_ids: selectedUsers,
        member_type: filterMemberType,
        only_unread: isUnreadOnly,
      });

      setDailyReports(prevReports => [...prevReports, ...paginate.data]);
      setCurrentPage(paginate.currentPage + 1);
      setLastPage(paginate.lastPage);
    } catch (error) {
      console.error('Failed to fetch daily reports:', error);
    } finally {
      setIsLoading(false);
    }
  };

  //スクロールのコールバック
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];

      console.log('target.isIntersecting', target.isIntersecting);
      if (target.isIntersecting) {
        fetchDailyReports();
      }
    },
    [isLoading, currentPage, lastPage]
  );

  const onReload = () => {
    console.log('リロード');
    if (isLoading) return;
    console.log('リロード開始');
    setCurrentPage(1);
    setLastPage(null);
    setDailyReports([]);
  };

  const fetchUsers = async () => {
    const result = await UserModel.fetchMyCompanyUsers({ is_public_report: true });
    setUsers(result);
  };

  /**
   * 全ての日報を既読にする
   */
  const handleAllRead = async () => {
    setAllReadModal(false);
    try {
      await DailyReportReadModel.allMarkAsRead();
      toast.success('既読にしました');
    } catch (error) {
      toast.error('既読の設定に失敗しました');
    }
    onReload();
  };

  const handleCloseDeleteModal = () => {
    setAllReadModal(false);
  };

  /**
   * 日報の既読状態を変更する
   * @param dailyReport
   * @param isRead
   */
  const handleReadStatusChange = (dailyReport: DailyReport, isRead: boolean) => {
    //該当の日報の既読状態を変更する
    const update = dailyReports.map(report => {
      if (report.id === dailyReport.id) {
        return {
          ...report,
          is_read: isRead,
        };
      }
      return report;
    });
    setDailyReports(update);
  };

  /******************
   * useEffect
   */
  /**ボトム ロード検知 */
  useEffect(() => {
    const option = {};

    // IntersectionObserverを作成し、指定したオプションとコールバック関数を設定
    const observer = new IntersectionObserver(handleObserver, option);

    // observerRefに現在の要素がある場合、それを監視する
    if (observerRef.current) observer.observe(observerRef.current);

    // クリーンアップ関数: コンポーネントがアンマウントされるときに監視を解除
    return () => {
      if (observerRef.current) observer.unobserve(observerRef.current);
    };
  }, [handleObserver]);

  useEffect(() => {
    // コールバック関数を定義
    const onReloadCallback = () => {
      // ここでリロード時の処理を実行
      onReload();
    };

    // コンポーネントがマウントされたときにリスナーを追加
    addListener(onReloadCallback);

    // コンポーネントがアンマウントされたときにリスナーを削除
    return () => {
      removeListener(onReloadCallback);
    };
  }, [addListener, removeListener]);

  useFocusEffect(
    React.useCallback(() => {
      console.log('Screen is focused!');
      onReload();

      DailyReportReadModel.allUnreadCount().then(count => {
        console.log('Unread count:', count);
      });

      const onReloadCallback = () => {
        // ここでリロード時の処理を実行
        onReload();
      };

      addListener(onReloadCallback);
      return () => {
        console.log('Screen is unfocused!');
      };
    }, [])
  );
  useEffect(() => {
    onReload();
  }, [filterMemberType, isUnreadOnly]);

  useEffect(() => {
    fetchUsers();
    fetchDailyReports();
  }, []);

  return (
    <Box sx={{ overflow: 'auto', height: '100vh' }}>
      {/* フィルタリング条件のUI */}
      <Paper elevation={3} style={{ padding: 5, marginBottom: 5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          {/* ラジオボタン (ステータス選択) */}
          <Box sx={{ borderRadius: 4, border: '1px solid #ccc', padding: 0.5 }}>
            <FormControl component="fieldset">
              {/* <Typography variant="subtitle1">メンバー</Typography> */}
              <RadioGroup row value={filterMemberType} onChange={handleStatusChange}>
                <FormControlLabel value="all" control={<Radio />} label="社内全員" />
                <FormControlLabel value="default" control={<Radio />} label="報告メンバーのみ" />
                <FormControlLabel value="select" control={<Radio />} label="指定" />
              </RadioGroup>
            </FormControl>

            {/* 複数選択可能なセレクトボタン (カテゴリ選択) */}
            {filterMemberType === 'select' && (
              <FormControl style={{ minWidth: 200 }}>
                <InputLabel>ユーザー</InputLabel>
                <Select multiple value={selectedUsers} onChange={handleUserChange}>
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <FormControlLabel
            control={
              <Checkbox checked={showComments} onChange={handleCommentToggle} color="primary" />
            }
            label={'コメント:開'}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isExpandedInvoice}
                onChange={handleInvoiceToggle}
                color="primary"
              />
            }
            label={'請求:略'}
          />
          <Box display="flex" alignItems="center" alignContent="center">
            <FormControlLabel
              control={
                <Checkbox checked={isUnreadOnly} onChange={handleUnreadToggle} color="primary" />
              }
              label={'未読のみ'}
            />
            <Box display="flex" alignContent="center" height="100%">
              <Button
                size="small"
                variant="contained"
                color="primary"
                style={{ marginTop: 8 }}
                onClick={() => {
                  setAllReadModal(true);
                }}
              >
                全て既読
              </Button>
            </Box>
          </Box>

          {/* 年月指定 (日付ピッカー) */}
          {/* <FormControl>
            <Typography variant="subtitle1">年月</Typography>
            <TextField
              type="month"
              value={selectedDate}
              onChange={handleDateChange}
            />
          </FormControl> */}

          {/* フィルタリングボタン */}
          <Button
            size="small"
            variant="contained"
            color="primary"
            style={{ marginTop: 8 }}
            onClick={onReload}
          >
            検索
          </Button>
        </Box>
      </Paper>
      <DailyReportsContext.Provider value={{ dailyReports, setDailyReports }}>
        <DailyReportInvoiceOpenContext.Provider value={{ isExpandedInvoice, setExpandedInvoice }}>
          <DailyReportCommentOpenContext.Provider value={{ showComments, setShowComments }}>
            {dailyReports.map(dailyReport => (
              <Box sx={{ py: 1, px: 1 }} key={dailyReport.id}>
                <DailyReportItem dailyReport={dailyReport} />
              </Box>
            ))}
          </DailyReportCommentOpenContext.Provider>
        </DailyReportInvoiceOpenContext.Provider>
      </DailyReportsContext.Provider>

      {/* ローディングインジケーター */}
      {isLoading && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      )}

      {/* スクロール終端の監視用要素 */}
      <div ref={observerRef} style={{ height: '20px' }} />

      {/* フローティングボタン */}
      <Fab
        color="primary"
        aria-label="add"
        style={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
        onClick={onhandleReportButton}
      >
        <Typography variant="body1">報告</Typography>
        {/* <AddIcon /> */}
      </Fab>

      {/* 全て既読モーダル */}
      <Dialog open={openAllReadModal} onClose={handleCloseDeleteModal}>
        <DialogTitle>確認</DialogTitle>
        <DialogContent>
          <DialogContentText>全ての日報を既読にしますか？</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} color="primary">
            キャンセル
          </Button>
          <Button onClick={handleAllRead} color="secondary">
            全て既読にする
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailyReportScreen;
